import { GlobalPositionStrategy, Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, DomPortal, TemplatePortal } from '@angular/cdk/portal';
import {
  ComponentRef,
  ElementRef,
  Inject,
  Injectable,
  Injector,
  OnDestroy,
  Renderer2,
  TemplateRef,
  ViewContainerRef
} from '@angular/core';
import { coerceElement } from '@angular/cdk/coercion';
import {
  MatSnackBarContainer,
  MatSnackBarRef,
  MAT_SNACK_BAR_DATA,
  MAT_SNACK_BAR_DEFAULT_OPTIONS,
  MatSnackBarConfig,
  MatSnackBarVerticalPosition
} from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';

import { NotificationComponent } from '../notification/notification.component';


/**
 * Notification Config
 */
export class NotificationConfig extends MatSnackBarConfig {
  horizontalPosition?: 'start' | 'center' | 'end' = 'center';
  verticalPosition?: MatSnackBarVerticalPosition = 'top';
  showClose?: boolean;
}


/**
 * Notification Service
 */
@Injectable({
  providedIn: 'root'
})
export class NotificationService implements OnDestroy {
  private notificationMap = new Map<App.NotificationPosition, App.NotificationStack[]>();
  private subscription = new Subscription();

  constructor(
    private injector: Injector,
    private overlay: Overlay,
    @Inject(MAT_SNACK_BAR_DEFAULT_OPTIONS) private defaultConfig: NotificationConfig,
  ) {
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  openNormal(
    msg: App.NotificationMessageType,
    config?: NotificationConfig
  ): MatSnackBarRef<NotificationComponent> {
    return this.open(msg, 'normal', config);
  }

  openInfo(
    msg: App.NotificationMessageType,
    config?: NotificationConfig
  ): MatSnackBarRef<NotificationComponent> {
    return this.open(msg, 'info', config);
  }

  openSuccess(
    msg: App.NotificationMessageType,
    config?: NotificationConfig
  ): MatSnackBarRef<NotificationComponent> {
    return this.open(msg, 'success', config);
  }

  openWarning(
    msg: App.NotificationMessageType,
    config?: NotificationConfig
  ): MatSnackBarRef<NotificationComponent> {
    return this.open(msg, 'warning', config);
  }

  openError(
    msg: App.NotificationMessageType,
    config?: NotificationConfig
  ): MatSnackBarRef<NotificationComponent> {
    return this.open(msg, 'error', config);
  }

  open(
    msg: App.NotificationMessageType,
    notificationType: App.NotificationType,
    userConfig?: NotificationConfig
  ): MatSnackBarRef<NotificationComponent> {
    const finalConfig = { ...new NotificationConfig(), ...this.defaultConfig, ...userConfig };

    finalConfig.data = {
      ...finalConfig.data,
      msg,
      notificationType
    };
    finalConfig.panelClass = 'mat-snack-bar-' + notificationType;

    const overlayRef = this.createOverlay(finalConfig);
    const container = this.attachSnackBarContainer(overlayRef, finalConfig);
    const snackBarRef = new MatSnackBarRef<NotificationComponent>(container, overlayRef);
    const injector = this.createInjector(finalConfig, snackBarRef);
    const portal = new ComponentPortal(NotificationComponent, undefined, injector);
    const contentRef = container.attachComponentPortal(portal);
    const hostEl = container['_elementRef'].nativeElement;
    const notificationStack = this.getNotificationStack(finalConfig);

    notificationStack.push({
      container,
      snackBarRef,
      overlayRef
    });

    snackBarRef.instance = contentRef.instance;
    this.animateSnackBar(snackBarRef, finalConfig);
    hostEl.style.margin = '0';

    return snackBarRef;
  }

  createPortal(content: HTMLElement | ElementRef): DomPortal;
  createPortal(
    content: TemplateRef<Record<string, unknown>>,
    viewContainer: ViewContainerRef
  ): TemplatePortal;
  createPortal(content: string, viewContainer: ViewContainerRef, renderer: Renderer2): DomPortal;
  createPortal(
    content: App.NotificationMessageType,
    viewContainer?: ViewContainerRef,
    renderer?: Renderer2
  ): DomPortal | TemplatePortal {
    if (typeof content === 'string') {
      const contentEl: HTMLElement = renderer.createElement('span');

      contentEl.textContent = content;
      renderer.appendChild(coerceElement(viewContainer.element), contentEl);

      return new DomPortal(contentEl);
    } else if (content instanceof TemplateRef) {
      return new TemplatePortal(content as TemplateRef<Record<string, unknown>>, viewContainer);
    } else {
      return new DomPortal(content);
    }
  }

  dismiss(): void {
    this.notificationMap.forEach((stack) => {
      const cloneItem = [...stack];

      stack.length = 0;
      cloneItem.forEach((item) => {
        item.snackBarRef.dismiss();
      });
    });
  }

  private animateSnackBar(
    snackBarRef: MatSnackBarRef<NotificationComponent>,
    config: NotificationConfig
  ) {
    snackBarRef.afterDismissed().subscribe(() => {
      const notificationStack = this.getNotificationStack(config);

      notificationStack.some((item, index) => {
        if (item.snackBarRef === snackBarRef) {
          notificationStack.splice(index, 1);
          return true;
        }
        return false;
      });
      this.updateSnackBarStackPosition(config);
    });

    snackBarRef.containerInstance.enter();

    if (config.duration && config.duration > 0) {
      snackBarRef.afterOpened().subscribe(() => {
        snackBarRef._dismissAfter(config.duration);
      });
    }
  }

  private attachSnackBarContainer(
    overlayRef: OverlayRef,
    config: NotificationConfig
  ): MatSnackBarContainer {
    const userInjector = config && config.viewContainerRef && config.viewContainerRef.injector;
    const injector = Injector.create({
      parent: userInjector || this.injector,
      providers: [{
        provide: MatSnackBarConfig,
        useValue: config
      }]
    });
    const containerPortal = new ComponentPortal(
      MatSnackBarContainer,
      config.viewContainerRef,
      injector
    );
    const containerRef: ComponentRef<MatSnackBarContainer> = overlayRef.attach(containerPortal);

    containerRef.instance.snackBarConfig = config;
    return containerRef.instance;
  }

  private createOverlay(config: NotificationConfig): OverlayRef {
    const notificationStack = this.getNotificationStack(config);
    const overlayConfig = new OverlayConfig({
      direction: config.direction,
      positionStrategy: this.getPositionStrategy(config, notificationStack)
    });

    return this.overlay.create(overlayConfig);
  }

  private createInjector<T>(config: NotificationConfig, snackBarRef: MatSnackBarRef<T>): Injector {
    const userInjector = config && config.viewContainerRef && config.viewContainerRef.injector;

    return Injector.create({
      parent: userInjector || this.injector,
      providers: [
        { provide: MatSnackBarRef, useValue: snackBarRef },
        { provide: MAT_SNACK_BAR_DATA, useValue: config.data }
      ]
    });
  }

  private updateSnackBarStackPosition(config: NotificationConfig): void {
    const position = (config.horizontalPosition +
      '-' +
      config.verticalPosition) as App.NotificationPosition;
    const snackBarStack = this.notificationMap.get(position);

    if (snackBarStack) {
      snackBarStack.forEach((item, index) => {
        const positionStrategy = this.getPositionStrategy(config, snackBarStack.slice(0, index));

        item.overlayRef.overlayElement.style.transition = '400ms cubic-bezier(.25, .8, .25, 1)';
        item.overlayRef.updatePositionStrategy(positionStrategy);
      });
    }
  }

  private getPositionStrategy(
    config: NotificationConfig,
    notificationStack: App.NotificationStack[]
  ): GlobalPositionStrategy {
    const positionStrategy = this.overlay.position().global();
    const isRtl = config.direction === 'rtl';
    const isLeft =
      (config.horizontalPosition === 'start' && !isRtl) ||
      (config.horizontalPosition === 'end' && isRtl);
    const isRight = !isLeft && config.horizontalPosition !== 'center';

    const betweenGap = '20px';
    const edgeGap = '20px';
    let top = `2rem + ${edgeGap} + ${betweenGap}`;
    let bottom = `20px + ${betweenGap}`;

    positionStrategy.centerHorizontally();

    notificationStack.forEach((item) => {
      const container = item.container['_elementRef'].nativeElement as HTMLElement;

      if (config.verticalPosition === 'top') {
        top += ` + ${container.clientHeight}px + ${betweenGap}`;
      } else {
        bottom += ` + ${container.clientHeight}px + ${betweenGap}`;
      }
    });

    if (config.verticalPosition === 'top') {
      positionStrategy.top(`calc(${top})`);
    } else {
      positionStrategy.bottom(`calc(${bottom})`);
    }

    return positionStrategy;
  }

  private getNotificationStack(config: NotificationConfig): App.NotificationStack[] {
    const position = (config.horizontalPosition +
      '-' +
      config.verticalPosition) as App.NotificationPosition;
    let stack = this.notificationMap.get(position);

    if (!stack) {
      stack = [];
      this.notificationMap.set(position, stack);
    }

    return stack;
  }
}
